import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, LOAN_PRODUCTS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export default function Apply() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1); // 1: mobile & OTP, 2: details, 3: done
  const [otpState, setOtpState] = useState({ phone: "", code: "", otp_ref: "", dev_code: "", requested: false, verifying: false });
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    city: "Siddipet",
    enquiry_type: params.get("type") === "insurance" ? "insurance" : "loan",
    loan_type: params.get("loan") || "personal_loan",
    insurance_type: "car",
    loan_amount: 500000,
    monthly_income: "",
    employment_type: "salaried",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const requestOtp = async () => {
    const p = otpState.phone.replace(/\D/g, "");
    if (p.length < 10) { toast.error("Enter a valid mobile number"); return; }
    try {
      const { data } = await api.post("/public/otp/request", { phone: p });
      setOtpState((s) => ({ ...s, requested: true, otp_ref: data.otp_ref, dev_code: data.dev_code || "" }));
      toast.success(data.throttled ? "OTP already sent — check your phone" : "OTP sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    }
  };

  const verifyOtp = async () => {
    if (otpState.code.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setOtpState((s) => ({ ...s, verifying: true }));
    try {
      await api.post("/public/otp/verify", { phone: otpState.phone.replace(/\D/g, ""), code: otpState.code });
      toast.success("Mobile verified");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    } finally {
      setOtpState((s) => ({ ...s, verifying: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error("Enter your full name"); return; }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name,
        phone: otpState.phone.replace(/\D/g, ""),
        email: form.email || null,
        city: form.city,
        enquiry_type: form.enquiry_type,
        loan_type: form.enquiry_type === "loan" ? form.loan_type : null,
        insurance_type: form.enquiry_type === "insurance" ? form.insurance_type : null,
        loan_amount: form.enquiry_type === "loan" ? Number(form.loan_amount) : null,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        employment_type: form.employment_type,
        notes: form.notes,
        otp_ref: otpState.otp_ref,
      };
      const { data } = await api.post("/public/apply", payload);
      setDone(data);
      setStep(3);
      toast.success(data.duplicate ? "Enquiry already recorded" : "Enquiry submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (step === 3 && done) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-xl mx-auto px-4 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#059669]" />
          <h1 className="mt-4 font-display text-3xl font-bold text-[#06152D]">Enquiry received</h1>
          <p className="mt-3 text-[#52525B]">Your reference number is</p>
          <div className="mt-2 font-mono-num text-xl font-bold text-[#0052FF]" data-testid="apply-reference">{done.lead_id}</div>
          <p className="mt-4 text-sm text-[#52525B]">A Loan Junction advisor will contact you within 24 hours on <span className="font-semibold">+91 {otpState.phone}</span>.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-[#F4F4F5] min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">Apply Online</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-[#06152D]">Start your enquiry</h1>
        <p className="mt-3 text-[#52525B]">All enquiries are verified via mobile OTP. Zero customer fees.</p>

        {step === 1 && (
          <div className="mt-8 bg-white border border-[#E4E4E7] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="apply-otp-step">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#52525B]">Step 1 of 2 · Verify Mobile</div>
            <div>
              <Label>Mobile Number *</Label>
              <Input value={otpState.phone} onChange={(e) => setOtpState({ ...otpState, phone: e.target.value })} placeholder="10-digit mobile" data-testid="apply-mobile" />
            </div>
            {!otpState.requested ? (
              <button onClick={requestOtp} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full" data-testid="apply-send-otp">
                Send OTP
              </button>
            ) : (
              <>
                {otpState.dev_code && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2" data-testid="apply-dev-otp">
                    <span className="font-semibold">Demo OTP:</span> <span className="font-mono-num">{otpState.dev_code}</span> (mock SMS — will be sent via real provider in production)
                  </div>
                )}
                <div>
                  <Label>Enter OTP *</Label>
                  <Input value={otpState.code} onChange={(e) => setOtpState({ ...otpState, code: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit code" data-testid="apply-otp-code" />
                </div>
                <div className="flex gap-3">
                  <button onClick={verifyOtp} disabled={otpState.verifying} className="flex-1 bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="apply-verify-otp">
                    <ShieldCheck size={16} /> {otpState.verifying ? "Verifying…" : "Verify & Continue"}
                  </button>
                  <button onClick={requestOtp} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] font-semibold px-4 py-3 rounded-full" data-testid="apply-resend-otp">
                    Resend
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="mt-8 bg-white border border-[#E4E4E7] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="apply-form">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
              <ShieldCheck size={14} /> Mobile Verified · +91 {otpState.phone}
            </div>
            <div>
              <Label>Enquiry Type *</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <label className={`border rounded-xl p-3 cursor-pointer text-sm font-medium ${form.enquiry_type === "loan" ? "border-[#0052FF] bg-[#EBF2FF] text-[#0052FF]" : "border-[#E4E4E7] text-[#06152D]"}`}>
                  <input type="radio" className="mr-2" checked={form.enquiry_type === "loan"} onChange={() => setForm({ ...form, enquiry_type: "loan" })} data-testid="apply-type-loan" />
                  Loan Enquiry
                </label>
                <label className={`border rounded-xl p-3 cursor-pointer text-sm font-medium ${form.enquiry_type === "insurance" ? "border-[#0052FF] bg-[#EBF2FF] text-[#0052FF]" : "border-[#E4E4E7] text-[#06152D]"}`}>
                  <input type="radio" className="mr-2" checked={form.enquiry_type === "insurance"} onChange={() => setForm({ ...form, enquiry_type: "insurance" })} data-testid="apply-type-insurance" />
                  Insurance Enquiry
                </label>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label>Full name *</Label>
                <Input value={form.full_name} onChange={set("full_name")} required data-testid="apply-full-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} data-testid="apply-email" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={form.city} onChange={set("city")} data-testid="apply-city" />
              </div>
              {form.enquiry_type === "loan" ? (
                <>
                  <div>
                    <Label>Loan type *</Label>
                    <Select value={form.loan_type} onValueChange={(v) => set("loan_type")(v)}>
                      <SelectTrigger data-testid="apply-loan-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOAN_PRODUCTS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Desired amount (₹) *</Label>
                    <Input type="number" value={form.loan_amount} onChange={set("loan_amount")} data-testid="apply-amount" />
                  </div>
                </>
              ) : (
                <div className="sm:col-span-2">
                  <Label>Insurance type *</Label>
                  <Select value={form.insurance_type} onValueChange={(v) => set("insurance_type")(v)}>
                    <SelectTrigger data-testid="apply-insurance-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">Car Insurance</SelectItem>
                      <SelectItem value="bike">Bike Insurance</SelectItem>
                      <SelectItem value="cv">Commercial Vehicle</SelectItem>
                      <SelectItem value="health">Health Insurance</SelectItem>
                      <SelectItem value="life">Life Insurance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Monthly income (₹)</Label>
                <Input type="number" value={form.monthly_income} onChange={set("monthly_income")} data-testid="apply-income" />
              </div>
              <div>
                <Label>Employment</Label>
                <Select value={form.employment_type} onValueChange={(v) => set("employment_type")(v)}>
                  <SelectTrigger data-testid="apply-employment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salaried">Salaried</SelectItem>
                    <SelectItem value="self_employed">Self-employed</SelectItem>
                    <SelectItem value="business_owner">Business owner</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Any additional notes</Label>
              <Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="apply-notes" />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-[#0052FF] hover:bg-[#003FCC] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full" data-testid="apply-submit">
              {submitting ? "Submitting…" : "Submit Enquiry"}
            </button>
            <p className="text-xs text-[#52525B] text-center">By submitting, you agree to Loan Junction's <a className="underline" href="/terms">Terms</a> and <a className="underline" href="/privacy">Privacy Policy</a>.</p>
          </form>
        )}
      </div>
    </section>
  );
}
